import { enumerateGroup, getMaxDepthChildrenInGroup } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/enumerate';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IFrame, IMultiModelFrameMap, IRectEdge, IRotateAndFlip } from '../frame.interface';
import { centerPointOfRect, checkChildrenNeedChangeWidthHeight, getFrameAfterRotate, pointAfterRotate } from '@src/slide/infrastructure/module/independent/graphic';
import TextAdjustManager from '@src/slide/domain/ui-model/element-modeles/property/text/text-adjust';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { ISmartArtModel } from '@src/slide/domain/ui-model/element-modeles/Interface/smart-art-model.interface';
import { isShapeAutoFit } from '@src/slide/domain/ui-model/element-modeles/property/text/text-adjust/utils';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { IAdjustedElementsAndProps } from './index.interface';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';
import { enumerateSimilarGroup } from '../../group-operation/enumerate';

interface IGroupFrameInfo {
    /**
     * groupModel.props.style的变更值
     */
    frame: IRect,
    /**
     * childFrame的作用是作为计算childElement位置的基准
     * 初始创建组合时，childFrame的值和groupModel.props.style大小相同
     * 当仅移动组合时，childFrame保持不变
     * 当改变组合子元素时，childFrame根据子元素变更的差值进行变更
     */
    childFrame: IRect,
    /**
     * 记录groupModel的差值变更信息，用于判断groupModel是否有变更
     */
    diffRect: IRect,
}

export class AdjustGroupFrameInfo {
    private rootGroupModel: IGroupModel | null;
    private adjustedGroupInfoArr: IAdjustedElementsAndProps[];

    /**
     *  获取组合子元素变更后，需要变更的组合的信息
     * @param childModels 有变更的组合子元素，隶属于同一个根组合
     * @param childModelsProps 变更的组合子元素对应更新的属性
     * @returns
     */
    public getAdjustedElementsAndProps(
        childModels: IElementBaseModel[],
        childModelsProps?: IFrame[],
    ): IAdjustedElementsAndProps[] {
        if (!childModels.length) {
            return [];
        }
        const updateFrameMap = this.getUpdateFrameMap(childModels, childModelsProps);
        this.adjustedGroupInfoArr = [];
        this.rootGroupModel = childModels[0].rootGroupModel;
        const filterChildModels = getMaxDepthChildrenInGroup(childModels);
        filterChildModels.forEach((childModel) => {
            enumerateGroup(childModel, (groupModel: IGroupModel) => {
                this.adjustGroupInfo(groupModel, updateFrameMap);
            });
        });
        this.rootGroupModel!.childResizeFinished = false;
        // 清空临时变量
        filterChildModels.forEach((childModel) => {
            enumerateGroup(childModel, (groupModel: IGroupModel) => {
                groupModel.operationFrame = null;
            });
        });
        return this.adjustedGroupInfoArr;
    }

    /**
     * 更新adjustedGroupInfoArr。根据子元素的frame变更信息，相应的调整组合的frame，并且再调整子元素的frame
     * @param groupModel
     * @param updateFrameMap
     * @returns
     */
    private adjustGroupInfo(groupModel: IGroupModel, updateFrameMap?: IMultiModelFrameMap): undefined | boolean {
        const groupOffsetInfos = this.getAdjustedGroupFrameCausedByChildren(groupModel, updateFrameMap);
        // 如果group 没有 变化 应该停止往父亲group 的修改
        if (!this.isGroupFrameChange(groupOffsetInfos.diffRect)) {
            groupModel.groupChildFrameChangedByChildren = null;
            return true;
        }
        const adjustedGroupInfo = {
            uiModel: groupModel,
            props: {
                style: { ...groupOffsetInfos.frame },
                childFrame: { ...groupOffsetInfos.childFrame },
            },
        };
        groupModel.operationFrame = { ...groupOffsetInfos.frame };
        groupModel.groupChildFrameChangedByChildren = { ...groupOffsetInfos.childFrame };
        this.addAdjusedGroupInfoArr(adjustedGroupInfo);
        this.modifyChildrenAfterGroupFrameAdjusted(groupModel, groupOffsetInfos, updateFrameMap);
    }

    /**
     *  获取因为children frame变更导致的group frame变更值
     * @param groupModel
     * @param updateFrameMap
     * @returns
     */
    private getAdjustedGroupFrameCausedByChildren(
        groupModel: IGroupModel,
        updateFrameMap?: IMultiModelFrameMap,
    ): IGroupFrameInfo {
        let childRectEdge;
        groupModel.children.forEach((childModel: IElementBaseModel) => {
            let childUpdatedFrame = {
                ...childModel.props.style,
                ...this.getTempFrame(childModel, updateFrameMap),
            };
            childUpdatedFrame = this.modifyChildFrameWithShapeAutoFit(childModel, childUpdatedFrame);
            childUpdatedFrame = this.checkChildModelNeedChangeWidthHeight(childModel.props.style)
                ? getFrameAfterRotate(childUpdatedFrame, 90)
                : childUpdatedFrame;
            childRectEdge = this.getChildRectEdge(childUpdatedFrame, childRectEdge);
        });
        return this.getGroupUpdatedFrame(groupModel, childRectEdge);
    }

    /**
     * 因group frame变更导致的children的top left变更
     * @param groupModel
     * @param groupOffsetInfos
     * @param updateFrameMap
     */
    private modifyChildrenAfterGroupFrameAdjusted(
        groupModel: IGroupModel, groupOffsetInfos: IGroupFrameInfo,
        updateFrameMap?: IMultiModelFrameMap,
    ) {
        groupModel.children.forEach((childModel: IElementBaseModel) => {
            const tempFrame = this.getTempFrame(childModel, updateFrameMap);
            const childUpdatedFrame = { ...childModel.props.style, ...tempFrame };
            const childSdkFrame = this.getSdkModelFrameInfo(childModel);
            const needAdjustedTopLeft = this.checkChildModelNeedChangeWidthHeight(childUpdatedFrame)
                !== this.checkChildModelNeedChangeWidthHeight(childSdkFrame);
            // 只对正在操作frame或者rotate触发width/height翻转的element进行更正，其它children不受影响
            if (tempFrame || needAdjustedTopLeft) {
                this.addAdjusedGroupInfoArr({
                    uiModel: childModel,
                    props: {
                        style: {
                            width: childUpdatedFrame.width,
                            height: childUpdatedFrame.height,
                            left: childUpdatedFrame.left - groupOffsetInfos.diffRect.left,
                            top: childUpdatedFrame.top - groupOffsetInfos.diffRect.top,
                        },
                    },
                });
            }
        });
    }

    /**
     * 形状设置了跟随文本大小自动调整，缩放结束时更新形状大小
     * @param childModel 子元素
     * @param childUpdatedFrame
     * @returns
     */
    private modifyChildFrameWithShapeAutoFit = (childModel: IElementBaseModel, childUpdatedFrame: IFrame): IFrame => {
        if (this.isShapeAutoFitChildResized(childModel) && childModel.operationFrame) {
            const { style } = TextAdjustManager.getInstance()
                .adjustShape(
                    childModel as any,
                    { style: childUpdatedFrame } as any,
                    this.getEditingStateManager().resizeMode,
                );
            childModel.operationFrame = style;
            childUpdatedFrame = style;
        }
        return childUpdatedFrame;
    };

    /**
     * 子元素是否触发长款对调
     * 当子元素存在旋转且为特定大小，需要根据flip进行处理，触发组合长宽对调
     */
    private checkChildModelNeedChangeWidthHeight = (frameInfo: IRotateAndFlip): boolean => {
        if (!frameInfo.rotate) return false;
        const flipH = frameInfo?.flipH === 1 ? 1 : 0;
        const flipV = frameInfo?.flipV === 1 ? 1 : 0;
        return checkChildrenNeedChangeWidthHeight(frameInfo.rotate, flipH, flipV);
    };

    /**
     * 获取子元素的边缘大小
     * @param childUpdatedFrame
     * @param childRectEdge
     * @returns
     */
    private getChildRectEdge = (childUpdatedFrame: IFrame, childRectEdge: IRectEdge): IRectEdge => {
        const childBounds = {
            left: childUpdatedFrame.left,
            top: childUpdatedFrame.top,
            right: childUpdatedFrame.left + childUpdatedFrame.width,
            bottom: childUpdatedFrame.top + childUpdatedFrame.height,
        };
        childRectEdge = childRectEdge ? {
            left: Math.min(childBounds.left, childRectEdge.left),
            top: Math.min(childBounds.top, childRectEdge.top),
            right: Math.max(childBounds.right, childRectEdge.right),
            bottom: Math.max(childBounds.bottom, childRectEdge.bottom),
        } : { ...childBounds };
        return childRectEdge;
    };

    /**
     * 获取组合待更新的frame信息
     * @param groupModel
     * @param childRectEdge
     */
    private getGroupUpdatedFrame = (groupModel: IGroupModel, childRectEdge: IRectEdge): IGroupFrameInfo => {
        const groupStyle = groupModel.props.style;
        const scale = groupModel.getFrame().getScaleRelativeToSlide();
        const groupUpdatedWidth = childRectEdge.right - childRectEdge.left;
        const groupUpdatedHeight = childRectEdge.bottom - childRectEdge.top;
        // flip 矫正 左右对调 or 上下对调
        const offsetRight = groupStyle.width - groupUpdatedWidth - childRectEdge.left;
        const offsetBottom = groupStyle.height - groupUpdatedHeight - childRectEdge.top;

        const groupFrameInfo = {
            frame: {
                left: groupStyle.left + (groupStyle.flipH ? offsetRight : childRectEdge.left),
                top: groupStyle.top + (groupStyle.flipV ? offsetBottom : childRectEdge.top),
                width: groupUpdatedWidth,
                height: groupUpdatedHeight,
            },
            childFrame: {
                left: (groupModel.props.childFrame?.left ?? 0) + (childRectEdge.left / scale.scaleX),
                top: (groupModel.props.childFrame?.top ?? 0) + (childRectEdge.top / scale.scaleY),
                width: groupUpdatedWidth / scale.scaleX,
                height: groupUpdatedHeight / scale.scaleY,
            },
            diffRect: {
                left: this.formatFloat(childRectEdge.left),
                top: this.formatFloat(childRectEdge.top),
                width: this.formatFloat(groupUpdatedWidth - groupStyle.width),
                height: this.formatFloat(groupUpdatedHeight - groupStyle.height),
            },
        };
        if (groupStyle.rotate) {
            const oldCenterPoint = centerPointOfRect(groupStyle);
            const newCenterPoint: any = centerPointOfRect(groupFrameInfo.frame);
            const rotateVal = groupStyle.rotate;
            const newRotateCenterPoint = pointAfterRotate(newCenterPoint, oldCenterPoint, rotateVal);
            const centerOffsetX = newRotateCenterPoint.x - newCenterPoint.x;
            const centerOffsetY = newRotateCenterPoint.y - newCenterPoint.y;
            groupFrameInfo.frame.left += centerOffsetX;
            groupFrameInfo.frame.top += centerOffsetY;
        }
        return groupFrameInfo;
    };

    private getSdkModelFrameInfo = (childModel: IElementBaseModel): IRotateAndFlip => {
        let rotate = 0;
        let flipH = 0;
        let flipV = 0;
        rotate = (childModel.sdkData?.props?.spPr?.xfrm?.rot ?? rotate) / 60000;
        flipH = Number(childModel.sdkData?.props?.spPr?.xfrm?.flipH ?? flipH);
        flipV = Number(childModel.sdkData?.props?.spPr?.xfrm?.flipV ?? flipV);
        return { rotate, flipH, flipV };
    };

    private isShapeAutoFitChildResized(childModel: IElementBaseModel) {
        return TextAdjustManager.getInstance().enableAdjustText() // 开关
            && isShapeAutoFit(childModel) && this.rootGroupModel?.childResizeFinished;
    }

    /**
     * 向adjustedGroupInfoArr数组中添加元素，避免重复添加
     * @param info
     */
    private addAdjusedGroupInfoArr(info: IAdjustedElementsAndProps) {
        let hasSameModel = false;
        this.adjustedGroupInfoArr = this.adjustedGroupInfoArr.map((item) => {
            if (item.uiModel === info.uiModel) {
                hasSameModel = true;
                // 如果groupModel作为父元素调整，props中包含childFrame，否则不包含
                return {
                    uiModel: info.uiModel,
                    props: { ...item.props, ...info.props },
                };
            }
            return item;
        });
        if (!hasSameModel) {
            this.adjustedGroupInfoArr.push(info);
        }
    }

    private isGroupFrameChange = (diffRect: IRect): boolean => !!(Object.keys(diffRect)
        .find(key => diffRect[key] !== 0));

    private getUpdateFrameMap = (
        childModels: IElementBaseModel[],
        childModelsProps?: any[],
    ): undefined | IMultiModelFrameMap => {
        let updateFrameMap;
        if (childModelsProps) {
            updateFrameMap = new Map();
            childModels.forEach((child, index) => {
                updateFrameMap.set(child.key, childModelsProps?.[index]);
            });
        }
        return updateFrameMap;
    };

    private getTempFrame = (
        childModel: IElementBaseModel,
        updateFrameMap?: IMultiModelFrameMap,
    ): Partial<IFrame> | null => updateFrameMap?.get(childModel.key) ?? childModel.operationFrame;

    private formatFloat = (value: number, fixedNum = 2): number => parseFloat(value.toFixed(fixedNum));

    private getEditingStateManager() {
        const appServices = AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices;
        return appServices.viewableServiceLayer.editingStateManager;
    }
}

export class AdjustSmartArtFrameInfo {
    private rootSmartArtModel: ISmartArtModel | null;
    private adjustedSmartArtInfoArr: IAdjustedElementsAndProps[];

    /**
     * 获取组合子元素变更后，需要变更的组合的信息
     * @param childModels 有变更的组合子元素，隶属于同一个根组合
     * @param childModelsProps 变更的组合子元素对应更新的属性
     * @returns
     */
    public getAdjustedElementsAndProps(
        childModels: IElementBaseModel[],
        childModelsProps?: IFrame[],
    ): IAdjustedElementsAndProps[] {
        const updateFrameMap = this.getUpdateFrameMap(childModels, childModelsProps);
        this.adjustedSmartArtInfoArr = [];
        this.rootSmartArtModel = childModels[0].rootSmartArtModel;
        const filterChildModels = childModels.slice(0, 1);      // SmartArt 没有更深层的结构
        filterChildModels.forEach((childModel) => {
            enumerateSimilarGroup(childModel, (smartArtModel: ISmartArtModel) => {
                this.adjustSmartArtInfo(smartArtModel, updateFrameMap);
            });
        });
        this.rootSmartArtModel!.childResizeFinished = false;
        // 清空临时变量
        filterChildModels.forEach((childModel) => {
            enumerateSimilarGroup(childModel, (smartArtModel: ISmartArtModel) => {
                smartArtModel.operationFrame = null;
            });
        });
        return this.adjustedSmartArtInfoArr;
    }

    /**
     * 更新 adjustedGroupInfoArr。根据子元素的frame变更信息，相应的调整组合的frame，并且再调整子元素的frame
     * @param groupModel
     * @param updateFrameMap
     * @returns
     */
    // eslint-disable-next-line max-len
    private adjustSmartArtInfo(smartArtModel: ISmartArtModel, updateFrameMap?: IMultiModelFrameMap): undefined | boolean {
        const smartArtOffsetInfos = this.getAdjustedSmartArtFrameCausedByChildren(smartArtModel, updateFrameMap);
        // 如果group 没有 变化 应该停止往父亲group 的修改
        if (!this.isSmartArtFrameChange(smartArtOffsetInfos.diffRect)) {
            smartArtModel.groupChildFrameChangedByChildren = null;
            return true;
        }
        const adjustedSmartArtInfo = {
            uiModel: smartArtModel,
            props: {
                style: { ...smartArtOffsetInfos.frame },
                childFrame: { ...smartArtOffsetInfos.childFrame },
            },
        };
        smartArtModel.operationFrame = { ...smartArtOffsetInfos.frame };
        smartArtModel.groupChildFrameChangedByChildren = { ...smartArtOffsetInfos.childFrame };
        this.addAdjusedSmartArtInfoArr(adjustedSmartArtInfo);
        this.modifyChildrenAfterSmartArtFrameAdjusted(smartArtModel, smartArtOffsetInfos, updateFrameMap);
    }

    private getUpdateFrameMap = (
        childModels: IElementBaseModel[],
        childModelsProps?: any[],
    ): undefined | IMultiModelFrameMap => {
        let updateFrameMap;
        if (childModelsProps) {
            updateFrameMap = new Map();
            childModels.forEach((child, index) => {
                updateFrameMap.set(child.key, childModelsProps?.[index]);
            });
        }
        return updateFrameMap;
    };

    /**
     * 因group frame变更导致的children的top left变更
     * @param groupModel
     * @param groupOffsetInfos
     * @param updateFrameMap
     */
    private modifyChildrenAfterSmartArtFrameAdjusted(
        smartArtModel: ISmartArtModel, smartArtOffsetInfos: IGroupFrameInfo,
        updateFrameMap?: IMultiModelFrameMap,
    ) {
        smartArtModel.shapeList.forEach((childModel: IElementBaseModel) => {
            const tempFrame = this.getTempFrame(childModel, updateFrameMap);
            const childUpdatedFrame = { ...childModel.props.style, ...tempFrame };
            const childSdkFrame = this.getSdkModelFrameInfo(childModel);
            const needAdjustedTopLeft = this.checkChildModelNeedChangeWidthHeight(childUpdatedFrame)
                !== this.checkChildModelNeedChangeWidthHeight(childSdkFrame);
            // 只对正在操作frame或者rotate触发width/height翻转的element进行更正，其它children不受影响
            if (tempFrame || needAdjustedTopLeft) {
                this.addAdjusedSmartArtInfoArr({
                    uiModel: childModel,
                    props: {
                        style: {
                            width: childUpdatedFrame.width,
                            height: childUpdatedFrame.height,
                            left: childUpdatedFrame.left - smartArtOffsetInfos.diffRect.left,
                            top: childUpdatedFrame.top - smartArtOffsetInfos.diffRect.top,
                        },
                    },
                });
            }
        });
    }

    private getSdkModelFrameInfo = (childModel: IElementBaseModel): IRotateAndFlip => {
        let rotate = 0;
        let flipH = 0;
        let flipV = 0;
        rotate = (childModel.sdkData?.props?.spPr?.xfrm?.rot ?? rotate) / 60000;
        flipH = Number(childModel.sdkData?.props?.spPr?.xfrm?.flipH ?? flipH);
        flipV = Number(childModel.sdkData?.props?.spPr?.xfrm?.flipV ?? flipV);
        return { rotate, flipH, flipV };
    };

    /**
     *  获取因为children frame变更导致的group frame变更值
     * @param groupModel
     * @param updateFrameMap
     * @returns
     */
    private getAdjustedSmartArtFrameCausedByChildren(
        smartArtModel: ISmartArtModel,
        updateFrameMap?: IMultiModelFrameMap,
    ): IGroupFrameInfo {
        let childRectEdge;
        smartArtModel.shapeList.forEach((childModel: IElementBaseModel) => {
            let childUpdatedFrame = {
                ...childModel.props.style,
                ...this.getTempFrame(childModel, updateFrameMap),
            };
            childUpdatedFrame = this.modifyChildFrameWithShapeAutoFit(childModel, childUpdatedFrame);
            childUpdatedFrame = this.checkChildModelNeedChangeWidthHeight(childModel.props.style)
                ? getFrameAfterRotate(childUpdatedFrame, 90) : childUpdatedFrame;
            childRectEdge = this.getChildRectEdge(childUpdatedFrame, childRectEdge);
        });
        return this.getSmartArtUpdatedFrame(smartArtModel, childRectEdge);
    }

    /**
     * 获取组合待更新的frame信息
     * @param groupModel
     * @param childRectEdge
     */
    private getSmartArtUpdatedFrame = (smartArtModel: ISmartArtModel, childRectEdge: IRectEdge): IGroupFrameInfo => {
        const smartArtStyle = smartArtModel.props.style;
        const scale = smartArtModel.getFrame().getScaleRelativeToSlide();
        const smartArtUpdatedWidth = childRectEdge.right - childRectEdge.left;
        const smartArtUpdatedHeight = childRectEdge.bottom - childRectEdge.top;
        // flip 矫正 左右对调 or 上下对调
        const offsetRight = smartArtStyle.width - smartArtUpdatedWidth - childRectEdge.left;
        const offsetBottom = smartArtStyle.height - smartArtUpdatedHeight - childRectEdge.top;

        const smartArtFrameInfo = {
            frame: {
                left: smartArtStyle.left + (smartArtStyle.flipH ? offsetRight : childRectEdge.left),
                top: smartArtStyle.top + (smartArtStyle.flipV ? offsetBottom : childRectEdge.top),
                width: smartArtUpdatedWidth,
                height: smartArtUpdatedHeight,
            },
            childFrame: {
                left: (smartArtModel.props.childFrame?.left ?? 0) + (childRectEdge.left / scale.scaleX),
                top: (smartArtModel.props.childFrame?.top ?? 0) + (childRectEdge.top / scale.scaleY),
                width: smartArtUpdatedWidth / scale.scaleX,
                height: smartArtUpdatedHeight / scale.scaleY,
            },
            diffRect: {
                left: this.formatFloat(childRectEdge.left),
                top: this.formatFloat(childRectEdge.top),
                width: this.formatFloat(smartArtUpdatedWidth - smartArtStyle.width),
                height: this.formatFloat(smartArtUpdatedHeight - smartArtStyle.height),
            },
        };
        if (smartArtStyle.rotate) {
            const oldCenterPoint = centerPointOfRect(smartArtStyle);
            const newCenterPoint: any = centerPointOfRect(smartArtFrameInfo.frame);
            const rotateVal = smartArtStyle.rotate;
            const newRotateCenterPoint = pointAfterRotate(newCenterPoint, oldCenterPoint, rotateVal);
            const centerOffsetX = newRotateCenterPoint.x - newCenterPoint.x;
            const centerOffsetY = newRotateCenterPoint.y - newCenterPoint.y;
            smartArtFrameInfo.frame.left += centerOffsetX;
            smartArtFrameInfo.frame.top += centerOffsetY;
        }
        return smartArtFrameInfo;
    };

    /**
     * 获取子元素的边缘大小
     * @param childUpdatedFrame
     * @param childRectEdge
     * @returns
     */
    private getChildRectEdge = (childUpdatedFrame: IFrame, childRectEdge: IRectEdge): IRectEdge => {
        const childBounds = {
            left: childUpdatedFrame.left,
            top: childUpdatedFrame.top,
            right: childUpdatedFrame.left + childUpdatedFrame.width,
            bottom: childUpdatedFrame.top + childUpdatedFrame.height,
        };
        childRectEdge = childRectEdge ? {
            left: Math.min(childBounds.left, childRectEdge.left),
            top: Math.min(childBounds.top, childRectEdge.top),
            right: Math.max(childBounds.right, childRectEdge.right),
            bottom: Math.max(childBounds.bottom, childRectEdge.bottom),
        } : { ...childBounds };
        return childRectEdge;
    };

    /**
     * 子元素是否触发长款对调
     * 当子元素存在旋转且为特定大小，需要根据flip进行处理，触发组合长宽对调
     */
    private checkChildModelNeedChangeWidthHeight = (frameInfo: IRotateAndFlip): boolean => {
        if (!frameInfo.rotate) return false;
        const flipH = frameInfo?.flipH === 1 ? 1 : 0;
        const flipV = frameInfo?.flipV === 1 ? 1 : 0;
        return checkChildrenNeedChangeWidthHeight(frameInfo.rotate, flipH, flipV);
    };

    /**
     * 形状设置了跟随文本大小自动调整，缩放结束时更新形状大小
     * @param childModel 子元素
     * @param childUpdatedFrame
     * @returns
     */
    private modifyChildFrameWithShapeAutoFit = (childModel: IElementBaseModel, childUpdatedFrame: IFrame): IFrame => {
        if (this.isShapeAutoFitChildResized(childModel) && childModel.operationFrame) {
            const { style } = TextAdjustManager.getInstance()
                .adjustShape(
                    childModel as any,
                    { style: childUpdatedFrame } as any,
                    this.getEditingStateManager().resizeMode,
                );
            childModel.operationFrame = style;
            childUpdatedFrame = style;
        }
        return childUpdatedFrame;
    };

    /**
     * 向adjustedGroupInfoArr数组中添加元素，避免重复添加
     * @param info
     */
    private addAdjusedSmartArtInfoArr(info: IAdjustedElementsAndProps) {
        let hasSameModel = false;
        this.adjustedSmartArtInfoArr = this.adjustedSmartArtInfoArr.map((item) => {
            if (item.uiModel === info.uiModel) {
                hasSameModel = true;
                // 如果groupModel作为父元素调整，props中包含childFrame，否则不包含
                return {
                    uiModel: info.uiModel,
                    props: { ...item.props, ...info.props },
                };
            }
            return item;
        });
        if (!hasSameModel) {
            this.adjustedSmartArtInfoArr.push(info);
        }
    }

    private isShapeAutoFitChildResized(childModel: IElementBaseModel) {
        return TextAdjustManager.getInstance().enableAdjustText() // 开关
            && isShapeAutoFit(childModel) && this.rootSmartArtModel?.childResizeFinished;
    }

    private isSmartArtFrameChange = (diffRect: IRect): boolean => !!(Object.keys(diffRect)
        .find(key => diffRect[key] !== 0));

    private getTempFrame = (
        childModel: IElementBaseModel,
        updateFrameMap?: IMultiModelFrameMap,
    ): Partial<IFrame> | null => updateFrameMap?.get(childModel.key) ?? childModel.operationFrame;

    private formatFloat = (value: number, fixedNum = 2): number => parseFloat(value.toFixed(fixedNum));

    private getEditingStateManager() {
        const appServices = AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices;
        return appServices.viewableServiceLayer.editingStateManager;
    }
}
